import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { X, Eraser, Check } from "lucide-react";

/**
 * Modal pad de signature — le client signe sur tablette, on récupère un data URL PNG.
 * Props:
 *  - onClose(): fermeture sans sauver
 *  - onSave(dataUrl, name): validation
 *  - defaultName?: string
 */
export default function SignaturePadModal({ onClose, onSave, defaultName = "" }) {
  const padRef = useRef(null);
  const [name, setName] = useState(defaultName);
  const [empty, setEmpty] = useState(true);

  const clear = () => { padRef.current?.clear(); setEmpty(true); };
  const save = () => {
    if (!padRef.current || padRef.current.isEmpty()) return;
    // trim + PNG data URL
    const dataUrl = padRef.current.getTrimmedCanvas().toDataURL("image/png");
    onSave(dataUrl, name.trim());
  };

  return (
    <div className="fixed inset-0 z-[60] bg-anthracite/80 flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-ivory max-w-2xl w-full rounded-rama-md shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
          <div>
            <div className="font-mono-caption text-xs text-bordeaux-700 tracking-widest">BON POUR ACCORD</div>
            <h3 className="font-display text-xl text-anthracite">Signature du client</h3>
          </div>
          <button data-testid="signature-close" onClick={onClose} className="p-2 hover:bg-black/5 rounded"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <label className="block text-sm">
            <span className="text-xs text-ink-soft tracking-wider uppercase">Nom du signataire</span>
            <input
              data-testid="signature-name"
              value={name} onChange={(e)=>setName(e.target.value)}
              placeholder="Ex : Sophie Durand"
              className="w-full p-2 border border-black/10 rounded mt-1 focus:outline-none focus:border-bordeaux-700"
            />
          </label>

          <div className="text-xs text-ink-soft">Faites signer votre client dans le cadre ci-dessous.</div>

          <div className="border-2 border-dashed border-bordeaux-700/30 rounded bg-white overflow-hidden">
            <SignatureCanvas
              ref={padRef}
              penColor="#1E1E1E"
              backgroundColor="rgba(255,255,255,1)"
              onBegin={()=>setEmpty(false)}
              canvasProps={{
                className: "w-full",
                width: 620, height: 240,
                "data-testid": "signature-canvas",
              }}
            />
          </div>

          <div className="flex justify-between items-center pt-2">
            <button data-testid="signature-clear" onClick={clear} className="btn-ghost text-sm"><Eraser size={14} /> Effacer</button>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-ghost text-sm">Annuler</button>
              <button
                data-testid="signature-save"
                onClick={save}
                disabled={empty}
                className={`btn-primary text-sm ${empty?"opacity-40 cursor-not-allowed":""}`}
              ><Check size={14} /> Valider la signature</button>
            </div>
          </div>
          <div className="text-[11px] text-ink-soft border-t border-black/5 pt-3">
            En validant, le client déclare accepter le devis dans son intégralité. La signature et l'horodatage sont enregistrés et apparaîtront sur le PDF.
          </div>
        </div>
      </div>
    </div>
  );
}
